<template>
  <section class="emotional-stability">
    <div class="emotional-stability__header">
      <h2 class="emotional-stability__title">
        Эмоциональная устойчивость и мотивация
      </h2>

      <div class="emotional-stability__filters">
        <a-select
            v-model:value="selectedGroup"
            :options="groupOptions"
            class="emotional-stability__select"
        />

        <div>
          <a-range-picker
              v-model:value="selectedPeriod"
              :placeholder="['Дата начала', 'Дата конца']"
              format="DD.MM.YYYY"
              value-format="YYYY-MM-DD"
              class="emotional-stability__period"
          />
        </div>
      </div>
    </div>

    <div class="emotional-stability__grid">
      <SmallCardWrapper
          name="Индекс устойчивости и мотивации"
          :helpText="'Индекс устойчивости и мотивации'"
      >
        <StabilityMotivationIndexCard
            :items="stabilityItems"
            :chartLabels="stabilityChart.labels"
            :chartDatasets="stabilityChart.datasets"
        />
      </SmallCardWrapper>

      <SmallCardWrapper
          name="Индекс хронической усталости"
          :helpText="'Индекс хронической усталости'"
      >
        <ChronicFatigueIndexCard
            :levels="fatigueLevels"
            :positions="fatiguePositions"
        />
      </SmallCardWrapper>
    </div>
  </section>
</template>

<script>
import { ref } from 'vue'
import SmallCardWrapper from '@/components/Widgets/WidgetCard.vue'
import StabilityMotivationIndexCard from "@/components/Analytics/StabilityMotivationIndexCard.vue";
import ChronicFatigueIndexCard from "@/components/Analytics/ChronicFatigueIndexCard.vue";
import {computed} from "@vue/runtime-core";
import {useStore} from "vuex";

export default {
  name: 'EmotionalStability',

  components: {
    ChronicFatigueIndexCard,
    StabilityMotivationIndexCard,
    SmallCardWrapper,
  },

  setup() {
    const store = useStore();
    const groups = computed(() => store.state.group.groups);

    const selectedGroup = ref('all')
    const selectedPeriod = ref()

    const groupOptions = computed(() => {
      return [
        {
          label: 'Все сотрудники',
          value: 'all',
        },
        ...groups.value.map((group) => ({
          label: group.name,
          value: group.id,
        })),
      ]
    });

    const periodOptions = [
      {
        label: '10.2025 - 12.2025',
        value: '10_2025_12_2025',
      },
      {
        label: '07.2025 - 09.2025',
        value: '07_2025_09_2025',
      },
      {
        label: '04.2025 - 06.2025',
        value: '04_2025_06_2025',
      },
    ]

    const stabilityItems = ref([
      {
        key: 'fatigue',
        label: 'Хроническая усталость',
        change: 6.1,
        value: 8.2,
        color: '#5C7CFA',
      },
      {
        key: 'stress',
        label: 'Индекс стресса',
        change: 4,
        value: 2,
        color: '#46C7D8',
        warning: 'Рекомендуется снизить нагрузку на специалистов',
      },
      {
        key: 'monotony',
        label: 'Индекс монотонии',
        change: -4,
        value: -1,
        color: '#E333C8',
      },
      {
        key: 'motivation',
        label: 'Снижение мотивации',
        change: 3.4,
        value: 5,
        color: '#B45CFF',
      },
    ])

    const stabilityChart = ref({
      labels: ['II кв.', 'III кв.', 'IV кв.', 'I кв.'],
      datasets: [
        {
          key: 'fatigue',
          label: 'Хроническая усталость',
          values: [-2, 3, -1, -0.5],
          color: '#5C7CFA',
        },
        {
          key: 'stress',
          label: 'Индекс стресса',
          values: [1, 11, 0.5, 3],
          color: '#46C7D8',
        },
        {
          key: 'monotony',
          label: 'Индекс монотонии',
          values: [-9, 2, -5.5, -3],
          color: '#E333C8',
        },
        {
          key: 'motivation',
          label: 'Снижение мотивации',
          values: [7, 6, 9, 7],
          color: '#B45CFF',
        },
      ],
    })

    const fatigueLevels = ref([
      {
        key: 'low',
        label: 'Низкий уровень',
        change: 7,
        value: 6,
        color: '#21AC0C',
      },
      {
        key: 'moderate',
        label: 'Умеренный',
        change: 10,
        value: 24,
        color: '#99DA6E',
      },
      {
        key: 'high',
        label: 'Высокий',
        change: 15,
        value: 42,
        color: '#FF9F40',
      },
      {
        key: 'critical',
        label: 'Критический',
        change: 3,
        value: 12,
        color: '#F55565',
      },
    ])

    const fatiguePositions = ref([
      {
        name: 'Линейный персонал',
        value: 3,
        max: 10,
        color: '#21AC0C',
      },
      {
        name: 'Специалисты',
        value: 8,
        max: 10,
        color: '#FF9F40',
        warning: 'Рекомендуется снизить нагрузку на специалистов',
      },
      {
        name: 'Старшие специалисты',
        value: 5,
        max: 10,
        color: '#99DA6E',
      },
      {
        name: 'Руководители',
        value: 1,
        max: 10,
        color: '#21AC0C',
      },
      {
        name: 'Топ-менеджмент',
        value: 10,
        max: 10,
        color: '#F55565',
        warning: 'Высокий риск хронической усталости',
      },
    ])

    return {
      selectedGroup,
      selectedPeriod,
      groupOptions,
      periodOptions,
      stabilityItems,
      stabilityChart,
      fatigueLevels,
      fatiguePositions,
    }
  },
}
</script>

<style scoped lang="scss">
.emotional-stability {
  width: 100%;
  text-align: start;
}

.emotional-stability__header {
  margin-bottom: 16px;
}

.emotional-stability__title {
  margin: 0 0 12px;
  font-weight: 500;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.emotional-stability__filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

.emotional-stability__select {
  width: 350px;
}

.emotional-stability__period {
  width: 200px;
}

.emotional-stability__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
}

.emotional-stability__grid :deep(.ant-card-body) {
  padding: 0;
}

.emotional-stability__grid :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

@media (max-width: $tablet-breakpoint) {
  .emotional-stability__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .emotional-stability__filters {
    flex-direction: column;
    align-items: stretch;
  }

  .emotional-stability__select,
  .emotional-stability__period {
    width: 100%;
  }
}
</style>